<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { page } from '$app/stores';

  import Accordion from '$components/accordion.svelte';
  import Skeleton from '$components/ui/skeleton.svelte';
  import CheckboxButton from '$components/ui/checkbox-button.svelte';
  import CardSelector from '$components/card-selector.svelte';

  import { shopify } from '$lib/shopify';
  import { getImageAttributes } from '$lib/utils/cms';
  import { CurrencyCode, FilterType, type Filter } from '$gql/storefront/schemas';
  import { encodePriceRange, parsePriceRangeLabel } from '$lib/utils/shopify/priceRange';
  import { filterConfigurationPriceRanges, resolveShopFilterOptions } from '$lib/stores/content';

  import type {
    GetCurrencyCodeQuery,
    GetCurrencyCodeQueryVariables
  } from '$gql/storefront/operations';

  /* GraphQL */ `
    query getCurrencyCode {
      products(first: 1) {
        nodes {
          priceRange {
            minVariantPrice {
              currencyCode
            }
          }
        }
      }
    }
  `;

  export let filter: Filter;

  $: resolvedFilter = filter ? resolveShopFilterOptions(filter) : undefined;
  $: customOptions = resolvedFilter?.values.filter((field) => !!field.relation);
  $: checkboxOptions = resolvedFilter?.values.filter((field) => !field.relation);

  // Nothing garantees that the collection loads without filters that produce 0 results, so there's no good way to get the Currency apart from this
  $: currencyCode = createQuery<GetCurrencyCodeQuery, GetCurrencyCodeQueryVariables>({
    enabled: filter.type === FilterType.PriceRange,
    staleTime: Infinity,
    queryKey: ['getCurrencyCode'],
    queryFn: async () => await $shopify.getCurrencyCode()
  });
  $: currency =
    $currencyCode.data?.products.nodes[0].priceRange.minVariantPrice.currencyCode ||
    CurrencyCode.Usd;

  $: filterQueryParams = $page.url.searchParams.getAll(filter.id);
</script>

<Accordion
  renderMode="always"
  initialOpen={filterQueryParams.length > 0}
  variant="filters"
  title={filter.label}
  badge={filterQueryParams.length > 0 ? filterQueryParams.length.toString() : undefined}
>
  {#if filter.type === FilterType.PriceRange}
    {#if $currencyCode.isLoading}
      <div class="flex gap-2 flex-wrap">
        {#each Array(4) as _}
          <Skeleton class="h-10 w-20 rounded-sm" />
        {/each}
      </div>
    {:else}
      <div class="flex gap-x-2 gap-y-2.5 flex-wrap">
        {#each $filterConfigurationPriceRanges as option, i (option)}
          {@const value = encodePriceRange(option)}
          {@const selected = filterQueryParams.includes(value)}
          <CheckboxButton
            form="filters"
            id={filter.id + i}
            label={$parsePriceRangeLabel(option, currency) || ''}
            name={filter.id}
            {value}
            checked={selected}
          />
        {/each}
      </div>
    {/if}
  {:else}
    <div class="flex flex-col gap-3">
      {#if !!customOptions?.length}
        <div class="grid grid-cols-[repeat(auto-fit,minmax(100px,1fr))] gap-2">
          {#each customOptions as option (option.id)}
            {#if option.relation?.image.filename}
              {@const { src } = getImageAttributes(option.relation?.image, { size: [128, 0] })}
              {@const selected = filterQueryParams.includes(option.label)}
              <CardSelector
                form="filters"
                size="sm"
                img={src}
                showTitle
                title={option.relation.name}
                id={filter.id + option.id}
                label={option.label}
                name={filter.id}
                value={option.label}
                checked={selected}
                class="h-20"
              />
            {/if}
          {/each}
        </div>
      {/if}

      {#if !!checkboxOptions?.length}
        <div class="flex gap-2 flex-wrap">
          {#each checkboxOptions as option (option.id)}
            {@const selected = filterQueryParams.includes(option.label)}
            <CheckboxButton
              form="filters"
              id={filter.id + option.id}
              label={option.label}
              name={filter.id}
              value={option.label}
              checked={selected}
            />
          {/each}
        </div>
      {/if}
    </div>
  {/if}
</Accordion>
